import React, { useState, useEffect } from 'react';
import FormTemplateService, { FormFieldElement, FormTemplateDefinition } from '../services/FormTemplateService';
import { FirebaseFormResponseService, FormFieldResponse, AuditDocument } from '../services/firebase/FirebaseFormResponseService';
import FormAssignmentService from '../services/FormAssignmentService';
import { FirebaseFormAssignmentService } from '../services/firebase/FirebaseFormAssignmentService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import VoiceInputButton from './VoiceInputButton';
import PHQ9 from './client/questionnaires/PHQ9';
import GAD7 from './client/questionnaires/GAD7';
import StandardIntakeQuestionnaire from './client/questionnaires/StandardIntakeQuestionnaire';
import CustomMeasureForm from './client/questionnaires/CustomMeasureForm';
import { FirebaseCustomMeasureService, CustomMeasure } from '../services/firebase/FirebaseCustomMeasureService';
import PDFGeneratorService, { PracticeInfo } from '../services/PDFGeneratorService';
import { doc as firestoreDoc, getDoc, collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

// Built-in template IDs that have dedicated components
const BUILT_IN_TEMPLATES = new Set(['phq9', 'gad7', 'standard-intake']);

interface FormViewerProps {
  assignmentId: string;
  clientId: string;
  formTemplateId: string;
  providerId?: string;
  onComplete: () => void;
  onCancel: () => void;
}

const FormViewer: React.FC<FormViewerProps> = ({
  assignmentId,
  clientId,
  formTemplateId,
  providerId: providerIdProp,
  onComplete,
  onCancel
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const formTemplateService = FormTemplateService.getInstance();
  const formAssignmentService = FormAssignmentService.getInstance();

  const [formTemplate, setFormTemplate] = useState<FormTemplateDefinition | null>(null);
  const [customMeasure, setCustomMeasure] = useState<CustomMeasure | null>(null);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [startTime] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadTemplate = async () => {
      const template = await formTemplateService.getFormTemplate(formTemplateId);
      if (!template) {
        setFormTemplate(null);
        return;
      }

      // Replace practice placeholders with real data
      const resolvedProviderId = providerIdProp || user?.uid || '';
      if (resolvedProviderId) {
        try {
          const practiceInfo = await PDFGeneratorService.fetchPracticeInfo(resolvedProviderId);
          const updatedElements = template.elements.map((el: any) => {
            if (el.type === 'description' && el.content?.text) {
              let text = el.content.text;
              text = text.replace('[PRACTICE NAME]', practiceInfo.practiceName);
              text = text.replace('[PRACTICE ADDRESS]', practiceInfo.address || '');
              const contactParts = [];
              if (practiceInfo.phone) contactParts.push(practiceInfo.phone);
              if (practiceInfo.email) contactParts.push(practiceInfo.email);
              text = text.replace('[PRACTICE CONTACT INFO]', contactParts.join(' | ') || '');
              if (text !== el.content.text) {
                return { ...el, content: { ...el.content, text } };
              }
            }
            return el;
          });
          setFormTemplate({ ...template, elements: updatedElements });
        } catch {
          setFormTemplate(template);
        }
      } else {
        setFormTemplate(template);
      }
    };
    loadTemplate();
  }, [formTemplateId]);

  // Load custom measure data if this is a custom measure assignment
  useEffect(() => {
    if (BUILT_IN_TEMPLATES.has(formTemplateId)) return;
    const loadCustomMeasure = async () => {
      try {
        const docSnap = await getDoc(firestoreDoc(db, 'custom_measures', formTemplateId));
        if (docSnap.exists()) {
          setCustomMeasure({ id: docSnap.id, ...docSnap.data() } as CustomMeasure);
        }
      } catch {
        // Not a custom measure — will fall through to regular form rendering
      }
    };
    loadCustomMeasure();
  }, [formTemplateId]);

  /**
   * Generate branded PDF, upload to Firebase Storage, and create client_files record
   */
  const generateAndUploadPDF = async (formResponse: any, resolvedProviderId: string) => {
    try {
      const FirebaseStorageService = require('../services/firebase/FirebaseStorageService').default;
      const pdfService = PDFGeneratorService.getInstance();
      const storageService = FirebaseStorageService.getInstance();

      // Fetch client info from Firestore
      const clientDoc = await getDoc(firestoreDoc(db, 'patients', clientId));
      if (!clientDoc.exists()) {
        console.error('Client not found in Firestore');
        return;
      }
      const clientData = clientDoc.data();
      const clientName = `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim() || 'Client';
      const clientDOB = clientData.dateOfBirth || '';

      // Fetch practice info from provider's Firestore doc
      let practiceInfo: PracticeInfo;
      if (resolvedProviderId) {
        practiceInfo = await PDFGeneratorService.fetchPracticeInfo(resolvedProviderId);
      } else {
        practiceInfo = {
          practiceName: 'Practice',
          providerName: 'Provider',
          credentials: '',
          licenseNumber: '',
          npiNumber: '',
          phone: '',
          email: '',
          address: '',
        };
      }

      // Extract signature from responses if present
      let signatureData;
      const signatureResponse = formResponse.responses?.find(
        (r: any) => r.questionType === 'signature' && r.answer
      );
      if (signatureResponse) {
        signatureData = {
          type: 'typed' as const,
          data: String(signatureResponse.answer),
          signedAt: formResponse.submittedAt || new Date().toISOString(),
        };
      }

      const formName = formResponse.formTemplateName || formTemplate?.name || formTemplateId;

      // Generate branded PDF
      const pdfBlob = pdfService.generateFormPDF({
        formResponse: {
          ...formResponse,
          formTemplateName: formName,
        },
        clientName,
        clientDOB,
        practiceInfo,
        signatureData,
        documentId: formResponse.id || assignmentId,
      });

      // Generate filename
      const filename = pdfService.generateFilename(
        clientName,
        clientDOB,
        formName,
        new Date()
      );

      // Upload to Firebase Storage
      const { downloadURL, fullPath } = await storageService.uploadFormPDF(
        practiceInfo.practiceName,
        clientName,
        clientDOB,
        filename,
        pdfBlob
      );

      // Create client_files Firestore record so PDF appears in provider's Files tab
      try {
        await addDoc(collection(db, 'client_files'), {
          clientId,
          providerId: resolvedProviderId,
          fileName: filename,
          category: 'Completed Forms',
          downloadUrl: downloadURL,
          storagePath: fullPath,
          fileSize: pdfBlob.size,
          contentType: 'application/pdf',
          uploadedAt: Timestamp.now(),
          uploadedBy: 'system',
        });
      } catch (filesError) {
        console.error('Error creating client_files record:', filesError);
      }

      console.log(`PDF uploaded: ${fullPath}`);
    } catch (error) {
      console.error('Error generating/uploading PDF:', error);
      // Don't throw - form submission should still succeed even if PDF upload fails
    }
  };

  // Handler for built-in form completion — saves to Firestore + marks assignment completed + generates PDF
  const handleBuiltInFormComplete = async (answers: Record<string, any>) => {
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);
    try {
      const templateInfo = FirebaseFormAssignmentService.getFormTemplateById(formTemplateId);
      const templateName = templateInfo?.name || customMeasure?.title || formTemplateId;
      const resolvedProviderId = providerIdProp || user?.uid || '';
      const completionTimeMinutes = Math.round((new Date().getTime() - startTime.getTime()) / 60000);

      // Question text mappings for built-in forms
      const questionTextMap: Record<string, Record<string, string>> = {
        'phq9': {
          question1: 'Little interest or pleasure in doing things.',
          question2: 'Feeling down, depressed, or hopeless.',
          question3: 'Trouble falling or staying asleep, or sleeping too much.',
          question4: 'Feeling tired or having little energy.',
          question5: 'Poor appetite or overeating.',
          question6: 'Feeling bad about yourself — or that you are a failure or have let yourself or your family down.',
          question7: 'Trouble concentrating on things, such as reading the newspaper or watching television.',
          question8: 'Moving or speaking so slowly that other people could have noticed. Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual.',
          question9: 'Thoughts that you would be better off dead or of hurting yourself in some way.',
          difficultyLevel: 'If you checked off any problem, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
        },
        'gad7': {
          question1: 'Feeling nervous, anxious, or on edge.',
          question2: 'Not being able to stop or control worrying.',
          question3: 'Worrying too much about different things.',
          question4: 'Trouble relaxing.',
          question5: 'Being so restless that it\'s hard to sit still.',
          question6: 'Becoming easily annoyed or irritable.',
          question7: 'Feeling afraid as if something awful might happen.',
          difficultyLevel: 'If you checked off any problem, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
        },
        'standard-intake': {
          counselingReason: 'What brings you to counseling?',
          counselingGoals: 'What are your goals for counseling?',
          previousMentalHealth: 'Have you previously received mental health services?',
          medications: 'Medications and supplements currently taking.',
          prescribingMD: 'Prescribing physician.',
          primaryCarePhysician: 'Primary care physician.',
          drinkAlcohol: 'Do you drink alcohol?',
          useRecreationalDrugs: 'Do you use recreational drugs?',
          suicidalThoughts: 'Have you ever had thoughts of suicide?',
          suicideAttempt: 'Have you ever attempted suicide?',
          harmOthersThoughts: 'Have you ever had thoughts of harming others?',
          psychiatricHospitalization: 'Have you ever been hospitalized for psychiatric reasons?',
          familyMentalHealth: 'Is there a history of mental illness in your family?',
          relationshipStatus: 'Relationship status.',
          livingSituation: 'Living situation.',
          educationLevel: 'Education level.',
          occupation: 'Occupation.',
          pastSixMonthsSymptoms: 'Symptoms experienced in the past 6 months.',
          medicalConditions: 'Current medical conditions.',
          additionalInfo: 'Additional information.',
        },
      };
      // Add custom measure questions to text map dynamically
      if (customMeasure) {
        const cmMap: Record<string, string> = {};
        customMeasure.questions.forEach(q => { cmMap[q.id] = q.text; });
        questionTextMap[formTemplateId] = cmMap;
      }
      const textMap = questionTextMap[formTemplateId] || {};

      // Convert answers to FormFieldResponse format with real question text
      const formFieldResponses: FormFieldResponse[] = Object.entries(answers).map(([key, value]) => ({
        questionId: key,
        questionText: textMap[key] || key,
        questionType: 'short-response',
        answer: Array.isArray(value) ? value.join(', ') : value
      }));

      // Save to Firestore
      let savedDocId = '';
      try {
        savedDocId = await FirebaseFormResponseService.saveResponse({
          assignmentId,
          clientId,
          providerId: resolvedProviderId,
          formTemplateId,
          formTemplateName: templateName,
          responses: formFieldResponses,
          submittedBy: clientId,
          completionTimeMinutes,
        });
      } catch (firestoreError) {
        console.error('Error persisting form response to Firestore:', firestoreError);
      }

      // Generate and upload PDF
      const builtInFormResponse = {
        id: savedDocId || assignmentId,
        assignmentId,
        clientId,
        formTemplateId,
        formTemplateName: templateName,
        responses: formFieldResponses,
        submittedAt: new Date().toISOString(),
        submittedBy: clientId,
        completionTimeMinutes,
      };
      await generateAndUploadPDF(builtInFormResponse, resolvedProviderId);

      // Mark assignment as completed
      await formAssignmentService.markFormAsCompleted(assignmentId);
      onComplete();
    } catch (error) {
      console.error('Error submitting built-in form:', error);
      toast.error('Submission Error', 'There was an error submitting your form. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Render built-in form components for known template IDs
  if (BUILT_IN_TEMPLATES.has(formTemplateId)) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {formTemplateId === 'phq9' && <PHQ9 onComplete={handleBuiltInFormComplete} />}
        {formTemplateId === 'gad7' && <GAD7 onComplete={handleBuiltInFormComplete} />}
        {formTemplateId === 'standard-intake' && <StandardIntakeQuestionnaire onComplete={handleBuiltInFormComplete} />}
      </div>
    );
  }

  // Render custom measure forms
  if (customMeasure) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <button onClick={onCancel} style={{
          marginBottom: '1rem', padding: '0.5rem 1rem', backgroundColor: '#374151',
          color: '#f9fafb', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem'
        }}>
          ← Cancel
        </button>
        <CustomMeasureForm
          measure={customMeasure}
          onComplete={handleBuiltInFormComplete}
        />
      </div>
    );
  }

  if (!formTemplate) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#d1d5db' }}>
        <p>Form template not found</p>
        <button onClick={onCancel} style={{
          marginTop: '1rem',
          padding: '0.5rem 1rem',
          backgroundColor: '#6b7280',
          color: 'white',
          border: 'none',
          borderRadius: '0.5rem',
          cursor: 'pointer'
        }}>
          Go Back
        </button>
      </div>
    );
  }

  const handleInputChange = (elementId: string, value: any) => {
    setResponses(prev => ({
      ...prev,
      [elementId]: value
    }));

    // Clear error when user starts typing
    if (errors[elementId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[elementId];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    formTemplate.elements.forEach(element => {
      if (element.content.required && element.type !== 'heading' && element.type !== 'description' && element.type !== 'divider') {
        const value = responses[element.id];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          newErrors[element.id] = 'This field is required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.warning('Required Fields', 'Please fill out all required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert responses to FormFieldResponse format
      const formFieldResponses: FormFieldResponse[] = formTemplate.elements
        .filter(element => element.type !== 'heading' && element.type !== 'description' && element.type !== 'divider')
        .map(element => ({
          questionId: element.id,
          questionText: element.content.question || element.content.title || element.content.text || '',
          questionType: element.type,
          answer: responses[element.id] || null
        }));

      // Calculate completion time
      let completionTimeMinutes: number | undefined;
      const now = new Date();
      completionTimeMinutes = Math.round((now.getTime() - startTime.getTime()) / 60000);

      // Save form response to Firestore
      const resolvedProviderId = providerIdProp || user?.uid || '';
      const savedDocId = await FirebaseFormResponseService.saveResponse({
        assignmentId,
        clientId,
        providerId: resolvedProviderId,
        formTemplateId,
        formTemplateName: formTemplate.name,
        responses: formFieldResponses,
        submittedBy: clientId,
        completionTimeMinutes,
      });

      // Generate and upload PDF to Firebase Storage
      const savedResponse = {
        id: savedDocId,
        assignmentId,
        clientId,
        formTemplateId,
        formTemplateName: formTemplate.name,
        responses: formFieldResponses,
        submittedAt: new Date().toISOString(),
        submittedBy: clientId,
        completionTimeMinutes,
      };
      await generateAndUploadPDF(savedResponse, resolvedProviderId);

      // Mark assignment as completed (async — Firestore-backed)
      await formAssignmentService.markFormAsCompleted(assignmentId);

      setIsSubmitting(false);
      onComplete();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Submission Error', 'There was an error submitting your form. Please try again.');
      setIsSubmitting(false);
    }
  };

  const renderFormElement = (element: FormFieldElement) => {
    const value = responses[element.id];
    const hasError = !!errors[element.id];

    switch (element.type) {
      case 'heading':
        return (
          <h3 key={element.id} style={{
            fontSize: '1.3rem',
            fontWeight: 700,
            color: '#0A2540',
            marginTop: '2rem',
            marginBottom: '1rem'
          }}>
            {element.content.title}
          </h3>
        );

      case 'description':
        return (
          <p key={element.id} style={{
            fontSize: '0.95rem',
            color: '#d1d5db',
            marginBottom: '1rem',
            lineHeight: '1.6'
          }}>
            {element.content.text}
          </p>
        );

      case 'divider':
        return (
          <hr key={element.id} style={{
            border: 'none',
            borderTop: '2px solid #e5e7eb',
            margin: '2rem 0'
          }} />
        );

      case 'short-response':
        return (
          <div key={element.id} style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#0A2540',
              marginBottom: '0.5rem'
            }}>
              {element.content.question}
              {element.content.required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={value || ''}
                onChange={(e) => handleInputChange(element.id, e.target.value)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  fontSize: '0.95rem',
                  border: hasError ? '2px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  backgroundColor: 'white'
                }}
                placeholder={element.content.placeholder || ''}
              />
              <VoiceInputButton onResult={(text) => handleInputChange(element.id, (value ? value + ' ' + text : text))} size={18} />
            </div>
            {hasError && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                {errors[element.id]}
              </p>
            )}
          </div>
        );

      case 'long-response':
        return (
          <div key={element.id} style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#0A2540',
              marginBottom: '0.5rem'
            }}>
              {element.content.question}
              {element.content.required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={value || ''}
                onChange={(e) => handleInputChange(element.id, e.target.value)}
                rows={4}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  fontSize: '0.95rem',
                  border: hasError ? '2px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  backgroundColor: 'white',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                placeholder={element.content.placeholder || ''}
              />
              <VoiceInputButton onResult={(text) => handleInputChange(element.id, (value ? value + ' ' + text : text))} size={20} />
            </div>
            {hasError && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                {errors[element.id]}
              </p>
            )}
          </div>
        );

      case 'single-option':
        return (
          <div key={element.id} style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#0A2540',
              marginBottom: '0.75rem'
            }}>
              {element.content.question}
              {element.content.required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {element.content.options?.map((option, index) => (
                <label key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  backgroundColor: value === option ? '#e0f2fe' : 'white'
                }}>
                  <input
                    type="radio"
                    name={element.id}
                    value={option}
                    checked={value === option}
                    onChange={(e) => handleInputChange(element.id, e.target.value)}
                    style={{ marginRight: '0.75rem', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.95rem', color: '#0A2540' }}>{option}</span>
                </label>
              ))}
            </div>
            {hasError && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                {errors[element.id]}
              </p>
            )}
          </div>
        );

      case 'multiple-options':
        return (
          <div key={element.id} style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#0A2540',
              marginBottom: '0.75rem'
            }}>
              {element.content.question}
              {element.content.required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {element.content.options?.map((option, index) => {
                const selectedOptions = value || [];
                const isChecked = selectedOptions.includes(option);

                return (
                  <label key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    backgroundColor: isChecked ? '#e0f2fe' : 'white'
                  }}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const newValue = e.target.checked
                          ? [...selectedOptions, option]
                          : selectedOptions.filter((o: string) => o !== option);
                        handleInputChange(element.id, newValue);
                      }}
                      style={{ marginRight: '0.75rem', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.95rem', color: '#0A2540' }}>{option}</span>
                  </label>
                );
              })}
            </div>
            {hasError && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                {errors[element.id]}
              </p>
            )}
          </div>
        );

      case 'dropdown':
        return (
          <div key={element.id} style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#0A2540',
              marginBottom: '0.5rem'
            }}>
              {element.content.question}
              {element.content.required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
            <select
              value={value || ''}
              onChange={(e) => handleInputChange(element.id, e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '0.95rem',
                border: hasError ? '2px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.5rem',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Select an option --</option>
              {element.content.options?.map((option, index) => (
                <option key={index} value={option}>{option}</option>
              ))}
            </select>
            {hasError && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                {errors[element.id]}
              </p>
            )}
          </div>
        );

      case 'date':
        return (
          <div key={element.id} style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#0A2540',
              marginBottom: '0.5rem'
            }}>
              {element.content.question}
              {element.content.required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
            <input
              type="date"
              value={value || ''}
              onChange={(e) => handleInputChange(element.id, e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '0.95rem',
                border: hasError ? '2px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.5rem',
                backgroundColor: 'white'
              }}
            />
            {hasError && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                {errors[element.id]}
              </p>
            )}
          </div>
        );

      case 'signature':
        return (
          <div key={element.id} style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#0A2540',
              marginBottom: '0.5rem'
            }}>
              {element.content.question}
              {element.content.required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleInputChange(element.id, e.target.value)}
              placeholder="Type your full name as signature"
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1.2rem',
                fontStyle: 'italic',
                border: hasError ? '2px solid #dc2626' : '2px solid #0A2540',
                borderRadius: '0.5rem',
                backgroundColor: '#f9fafb',
                fontFamily: 'Brush Script MT, cursive'
              }}
            />
            {hasError && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                {errors[element.id]}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: 'white',
      borderRadius: '1rem',
      padding: '2rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    }}>
      {/* Header */}
      <div style={{
        borderBottom: '3px solid #0A2540',
        paddingBottom: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 700,
          color: '#0A2540',
          marginBottom: '0.5rem'
        }}>
          {formTemplate.name}
        </h1>
        {formTemplate.description && (
          <p style={{
            fontSize: '1rem',
            color: '#d1d5db',
            marginTop: '0.5rem'
          }}>
            {formTemplate.description}
          </p>
        )}
        <div style={{
          marginTop: '1rem',
          display: 'flex',
          gap: '1rem',
          alignItems: 'center'
        }}>
          <span style={{
            display: 'inline-block',
            padding: '0.25rem 0.75rem',
            backgroundColor: '#e0f2fe',
            color: '#0c4a6e',
            borderRadius: '1rem',
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'capitalize'
          }}>
            {formTemplate.category.replace('-', ' ')}
          </span>
          {formTemplate.required && (
            <span style={{
              display: 'inline-block',
              padding: '0.25rem 0.75rem',
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              borderRadius: '1rem',
              fontSize: '0.85rem',
              fontWeight: 600
            }}>
              Required
            </span>
          )}
        </div>
      </div>

      {/* Form Elements */}
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {formTemplate.elements.map(element => renderFormElement(element))}

        {/* Action Buttons */}
        <div style={{
          marginTop: '3rem',
          paddingTop: '2rem',
          borderTop: '2px solid #e5e7eb',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: '#d1d5db',
              backgroundColor: 'white',
              border: '2px solid #d1d5db',
              borderRadius: '0.5rem',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: 'white',
              backgroundColor: '#226F9E',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Form'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormViewer;
